import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

import {
    ChangePasswordRequestDto,
    RefreshTokenRequestDto,
} from '../models/environment.model';
import { User } from '../models/user.model';
import { LooseArrayEnvelope } from '../models/api-envelope.model';
import { IdentityMenuModuleDto } from '../models/auth-api.model';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';
import { SessionService } from './session.service';
import { UserContextService } from './user-context.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly logger = inject(LoggerService);
    private readonly sessionService = inject(SessionService);
    private readonly userContext = inject(UserContextService);

    readonly currentUser = this.sessionService.currentUser;
    readonly isAuthenticated = this.sessionService.isAuthenticated;

    private isRefreshing = false;
    private readonly refreshSubject = new BehaviorSubject<string | null>(null);

    constructor() {
        const restored = this.sessionService.restoreSession();
        if (restored?.user && !restored.user.profileNames) {
            const tokenProfileNames = this.userContext.getProfileNamesFromToken(restored.token);
            if (tokenProfileNames.length > 0) {
                this.sessionService.setSession(
                    { ...restored.user, profileNames: tokenProfileNames },
                    restored.token,
                    restored.refreshToken,
                    restored.expiresAt,
                );
            }
        }
    }

    startTokenRefresh(): boolean {
        if (this.isRefreshing) {
            return false;
        }

        this.isRefreshing = true;
        this.refreshSubject.next(null);
        return true;
    }

    completeTokenRefresh(token: string | null): void {
        this.isRefreshing = false;
        this.refreshSubject.next(token);
    }

    waitForTokenRefresh(): Observable<string> {
        return this.refreshSubject.pipe(
            filter((token): token is string => token !== null),
            take(1),
        );
    }

    login(email: string, password: string, remember = false): Observable<boolean> {
        this.sessionService.clearSession();

        return this.http.post<unknown>(`${environment.identityApiUrl}/identity/auth/login`, { email, password }).pipe(
            map((response) => this.userContext.normalizeLoginResponse(response)),
            switchMap((response) => {
                if (!response) {
                    throw new Error('Login response does not contain token information');
                }

                return this.fetchCurrentUser(response.token).pipe(
                    tap((user) => {
                        this.sessionService.setSession(
                            user,
                            response.token,
                            response.refreshToken,
                            response.expiresAt,
                            remember,
                        );
                    }),
                    map(() => true),
                );
            }),
            catchError((error) => {
                this.logger.error('AuthService: Login failed', error);
                this.sessionService.clearSession();
                return of(false);
            }),
        );
    }

    loadCurrentUser(): Observable<User | null> {
        const sessionUser = this.currentUser();
        if (sessionUser && this.isAuthenticated()) {
            return of(sessionUser);
        }

        const token = this.getToken();
        if (!token) {
            return of(null);
        }

        return this.fetchCurrentUser(token).pipe(
            tap((user) => {
                this.sessionService.setSession(
                    user,
                    token,
                    this.getRefreshToken(),
                    this.sessionService.getSessionExpiresAt(),
                );
            }),
            catchError((error) => {
                this.logger.error('AuthService: Authentication failed', error);
                this.sessionService.clearSession();
                return of(null);
            }),
        );
    }

    refreshCurrentUser(): Observable<User | null> {
        const token = this.getToken();
        if (!token) {
            this.sessionService.clearSession();
            return of(null);
        }

        return this.fetchCurrentUser(token).pipe(
            tap((user) => {
                this.sessionService.setSession(
                    user,
                    token,
                    this.getRefreshToken(),
                    this.sessionService.getSessionExpiresAt(),
                );
            }),
            catchError((error) => {
                this.logger.error('AuthService: Failed to refresh current user', error);
                return of(null);
            }),
        );
    }

    requestPasswordReset(email: string): Observable<boolean> {
        return this.http.post(`${environment.identityApiUrl}/identity/auth/forgot-password`, { email }).pipe(
            map(() => true),
            catchError((error) => {
                this.logger.error('AuthService: Forgot password request failed', error);
                return of(false);
            }),
        );
    }

    resetPassword(email: string, token: string, newPassword: string): Observable<boolean> {
        return this.http.post(`${environment.identityApiUrl}/identity/auth/reset-password`, { email, token, newPassword }).pipe(
            map(() => true),
            catchError((error) => {
                this.logger.error('AuthService: Reset password request failed', error);
                return of(false);
            }),
        );
    }

    getToken(): string {
        return this.sessionService.getToken();
    }

    getRefreshToken(): string {
        return this.sessionService.getRefreshToken();
    }

    logout(): void {
        this.sessionService.clearSession();
    }

    isRbiAdmin(): boolean {
        return this.userContext.isRbiAdmin(this.currentUser(), this.getToken());
    }

    isSuperUser(): boolean {
        return this.userContext.isSuperUser(this.currentUser(), this.getToken());
    }

    refreshAuthToken(): Observable<boolean> {
        const request: RefreshTokenRequestDto = {
            token: this.getToken(),
            refreshToken: this.getRefreshToken(),
        };

        return this.http.post<unknown>(`${environment.identityApiUrl}/identity/auth/refresh`, request).pipe(
            map((response) => this.userContext.normalizeLoginResponse(response)),
            switchMap((response) => {
                if (!response) {
                    throw new Error('Refresh response does not contain token information');
                }

                return this.fetchCurrentUser(response.token).pipe(
                    tap((user) => {
                        this.sessionService.setSession(
                            user,
                            response.token,
                            response.refreshToken,
                            response.expiresAt,
                        );
                    }),
                    map(() => true),
                );
            }),
            catchError((error) => {
                this.logger.error('AuthService: Token refresh failed', error);
                this.sessionService.clearSession();
                return of(false);
            }),
        );
    }

    changePassword(currentPassword: string, newPassword: string): Observable<boolean> {
        const request: ChangePasswordRequestDto = { currentPassword, newPassword };
        return this.http.post(`${environment.identityApiUrl}/identity/auth/change-password`, request).pipe(
            map(() => true),
            catchError((error) => {
                this.logger.error('AuthService: Change password failed', error);
                return of(false);
            }),
        );
    }

    updateCurrentUser(patch: Partial<User>): void {
        const current = this.currentUser();
        if (!current) {
            return;
        }

        const token = this.getToken();
        const refreshToken = this.getRefreshToken();
        const expiresAt = this.sessionService.getSessionExpiresAt();
        const nextUser = this.userContext.applyUserPatch(current, patch);

        this.sessionService.setSession(nextUser, token, refreshToken, expiresAt);
    }

    getProfileNamesFromToken(): string[] {
        return this.userContext.getProfileNamesFromToken(this.getToken());
    }

    private fetchCurrentUser(token: string): Observable<User> {
        const parsedToken = this.userContext.parseJwtPayload(token) || {};
        const isSuperUser = this.userContext.isSuperUserFromPayload(parsedToken);
        const tokenSub = String(parsedToken['sub'] ?? '').trim();
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        if (isSuperUser || !tokenSub) {
            const apiUser = this.userContext.buildSuperUserFromToken(parsedToken);
            if (!apiUser) {
                throw new Error('No user found in response');
            }

            return this.fetchPermissionsFromMenu(token).pipe(
                map((menuPermissions) => this.userContext.mapApiUser(apiUser, token, menuPermissions)),
            );
        }

        return this.http
            .get<unknown>(`${environment.identityApiUrl}/identity/user/${tokenSub}`, { headers })
            .pipe(
                switchMap((response) => {
                    const apiUser = this.userContext.extractCurrentUser(response, parsedToken);
                    if (!apiUser) {
                        throw new Error('No user found in response');
                    }

                    return this.fetchPermissionsFromMenu(token).pipe(
                        map((menuPermissions) => this.userContext.mapApiUser(apiUser, token, menuPermissions)),
                    );
                }),
            );
    }

    private fetchPermissionsFromMenu(token: string): Observable<string[]> {
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        return this.http
            .get<LooseArrayEnvelope<IdentityMenuModuleDto>>(`${environment.identityApiUrl}/identity/user/menu`, { headers })
            .pipe(
                map((response) => this.userContext.extractPermissionsFromMenu(response.results ?? [])),
                catchError((error) => {
                    this.logger.warn('AuthService: Failed to load /identity/user/menu for permissions fallback', error);
                    return of([]);
                }),
            );
    }
}

